import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuTrigger } from '@fluentui/react-components';
import { Add16Regular, ChevronDown12Regular } from '@fluentui/react-icons';
import { paths } from '@/routes';
import { usePillStyles } from './pillStyles';
import { useMenuList } from './useMenuList';
import { PillMenu } from './PillMenu';
import type { AutomationProject } from '@/types/automation';

interface WorkspacePillProps {
  /** Current location (Azure region) name from URL params. Used only to
   *  detect the currently-selected project; cross-region projects in the
   *  dropdown use their own `location` for navigation. */
  locationName: string;
  /** Current project name from URL params. */
  projectName: string;
  /** All projects the user has access to in the current tenant (across
   *  every region). The dropdown lists them flat with no region grouping. */
  projects: readonly AutomationProject[];
  /** Tenant id for navigation, undefined if no tenant prefix is needed. */
  tenantId?: string | null | undefined;
  /** When true, the user is already on the project landing route, so the
   *  "Go to project" affordance on the current row is suppressed. */
  isAtProjectRoot?: boolean;
  /** When provided (and `canCreate` is true), the dropdown renders a
   *  "+ Create new project" footer that invokes this callback. */
  onCreate?: () => void;
  /** Gates the create footer for users without the right permission. Defaults
   *  to true; pass false to hide the footer even when `onCreate` is supplied. */
  canCreate?: boolean;
  /** Telemetry callback fired when the user selects a project. */
  onProjectChange?: (projectName: string) => void;
}

/**
 * Project-focused workspace chip.
 *
 * The pill itself shows only the current project. The dropdown lists every
 * project the user can reach in the current tenant, regardless of region;
 * picking a project from another region routes to that project using its
 * own `location` field, so cross-region jumps work.
 *
 * Region switching is intentionally not exposed here — it's a rare operation
 * that was cluttering the high-frequency project switcher.
 */
export const WorkspacePill = ({
  locationName,
  projectName,
  projects,
  tenantId,
  isAtProjectRoot,
  onCreate,
  canCreate = true,
  onProjectChange,
}: WorkspacePillProps) => {
  const styles = usePillStyles();
  const navigate = useNavigate();

  // Match the current project both by name and region so a project with the
  // same name in a different tenant region doesn't get mis-marked as current.
  const isCurrentProject = useCallback(
    (project: AutomationProject) =>
      project.name === projectName && project.location === locationName,
    [projectName, locationName],
  );

  const getProjectName = useCallback((p: AutomationProject) => p.name, []);
  const {
    search,
    setSearch,
    onMenuOpenChange,
    visible: visibleProjects,
    isSearching,
  } = useMenuList(projects, getProjectName, isCurrentProject);

  const handleProjectClick = useCallback(
    (project: AutomationProject) => {
      // Clicking the current project navigates to its apps list ("Go to
      // project" affordance). Picking a different project uses that
      // project's region, not the current URL's region, so cross-region
      // selections route correctly.
      if (!isCurrentProject(project)) onProjectChange?.(project.name);
      navigate(paths.projectApps(project.location, project.name, tenantId));
    },
    [isCurrentProject, tenantId, navigate, onProjectChange],
  );

  return (
    <Menu onOpenChange={onMenuOpenChange}>
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.workspacePill}
          aria-label={`Project ${projectName}. Click to switch project.`}
        >
          <span className={styles.pillPrefix} aria-hidden="true">Project:</span>
          <span className={styles.pillLabel}>{projectName}</span>
          <ChevronDown12Regular className={styles.pillChevron} aria-hidden="true" />
        </button>
      </MenuTrigger>
      <PillMenu
        sectionTitle="Projects"
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search projects…"
        searchAriaLabel="Search projects"
        items={visibleProjects}
        getKey={(p) => p.id}
        getLabel={(p) => p.name}
        isCurrent={isCurrentProject}
        onSelect={handleProjectClick}
        isSearching={isSearching}
        emptyCopy="No projects"
        noMatchCopy="No matching projects"
        {...(isAtProjectRoot ? {} : { currentItemActionLabel: 'Go to project' })}
        {...(canCreate && onCreate
          ? {
              viewAllLabel: 'Create new project',
              viewAllIcon: <Add16Regular aria-hidden="true" />,
              onViewAll: onCreate,
            }
          : {})}
      />
    </Menu>
  );
};
